import React from "react"
import {
	Text,
	Flex,
	Button,
	TextField,
	Checkbox,
	FilterCheckbox,
	Radio,
	Icon,
} from "@capioweb/ui-components/src"
import { LocalesContext } from "../../context"
import { SharedFormProps } from "../../@types/CapioWeb/core"
import { postAccountForm } from "../../components/MyPages/api/postAccountForm"
import { useForm } from "react-hook-form"
import {
	CapioSharedLoginFormWideStyled,
} from "../../pages/CapioLoginPage/styled"
import styled, { keyframes } from 'styled-components'

export interface LifestyleFormProps {
	lifestyleForm: SharedFormProps
}

export interface LoadingProps {
	loading: boolean
}

export const spin = keyframes`
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
	`

export const StyledIcon = styled.div<LoadingProps>`
	width: 24px;
	opacity: ${(props) => props.loading ? 1 : 0};
	position: absolute;
	& svg {
		width: 100%;
		height: 100%;
		animation: ${spin} 2s linear infinite; /* 2s is the duration, adjust as needed */
	}
	`
export const StyledButtonContent = styled.span<LoadingProps>`
		opacity: ${(props) => props.loading ? 0 : 1};
`

const LifestyleForm: React.FC<LifestyleFormProps> = (props) => {
	const [loading, setLoading] = React.useState<boolean>(false)
	// State for feedback message on form error/success
	const [feedbackMsg, setFeedbackMsg] =
		React.useState<{
			success: boolean
			message: string
		}>(null)

	// Register form
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		mode: "onChange",
	})

	const { translate, translationKeys } = React.useContext(LocalesContext)

	// Submit handler
	const onSubmit = async (data) => {
		setFeedbackMsg(null)
		setLoading(true)

		const { action, antiForgeryToken } = props.lifestyleForm
		try {
			const result = await postAccountForm(
				action,
				antiForgeryToken,
				data,
				props.lifestyleForm.method
			)

			if (result.success) {
				setFeedbackMsg({
					success: true,
					message: translate(translationKeys.manageAccountFeedbackMessageSuccess),
				})
			} else {
				setFeedbackMsg({
					success: false,
					message: result.data?.message ?? result.statusCode,
				})
			}
		} catch (e) {
			console.error("---Catch", e, "--- error")
		}

		setLoading(false)
	}


	return (
		<CapioSharedLoginFormWideStyled
			action={props.lifestyleForm.action}
			encType="application/x-www-form-urlencoded"
			method={props.lifestyleForm.method}
			onSubmit={handleSubmit(onSubmit)}
		>
			<Flex
				w={1}
				flexDirection="column"
				pt={{ _: 3, m: 5 }}
				pb={2}
				px={{ _: 3, m: 7 }}
			>
				<Flex flexDirection="column" pb={2}>

					{props.lifestyleForm.heading && (
						<Text variant="headline-1" mb={{ _: 1, m: 3 }}>
							{props.lifestyleForm.heading}
						</Text>
					)}
					{props.lifestyleForm.description && (
						<Text>{props.lifestyleForm.description}</Text>
					)}

				</Flex>
				{props.lifestyleForm?.fields?.map((field, i) => {
					const isInputField = !["rte", "captcha", "submit"].includes(
						field.type
					)

					if (field.type && isInputField) {
						const { type, value, required, ...rest } = field
						// https://react-hook-form.com/api/useform/register
						const { ref, ...registerProps } = register(field.name, {
							required: {
								value:
									field.type !== "checkbox" || field.name === "terms"
										? true
										: false,
								message: props.lifestyleForm.requiredMessage,
							},
						})
						const fieldProps = {
							...registerProps,
							...rest,
							type,
							key: field.id,
							error: errors[field.name],
							...(field.type !== "hidden" &&
								errors[field.name] && {
								helperText: errors[field.name].message,
							}),
						}

						if (["text", "email", "tel"].includes(type)) {
							return (
								<TextField
									inputRef={ref}
									defaultValue={value}
									{...fieldProps}
								/>
							)
						} else {
							return {
								checkbox:
									field.items && field.items.length > 1 ? (
										<>
											<Text as="p" fontWeight='bold' mb={3}>{field.label}</Text>
											<Flex
												key={field.id}
												as="fieldset"
												flexWrap="wrap"
												rowGap={3}
												columnGap={1.5}
											>

												{field.items.map((item, i) => {
													const { checked, ...restItem } = item

													const { ref, ...rest } = register(field.name)

													return (
														<Flex key={item.value}>
															<FilterCheckbox
																inputRef={ref}
																id={field.name + "-" + item.value}
																name={field.name}
																label={item.caption}
																value={item.value}
																defaultChecked={
																	field.items?.length > 0 &&
																	field.items[i].checked
																}
																{...restItem}
																{...rest}
															/>
														</Flex>
													)
												})}
											</Flex>
										</>
									) : (
										<Flex key={field.id} mb={2} justifyContent="space-between">
											<Checkbox
												inputRef={ref}
												value={field.value ?? field.label}
												defaultChecked={
													field.items?.length > 0 && field.items[0].checked
												}
												{...fieldProps}
											/>
										</Flex>
									),
								radio:
									field.items && field.items.length > 1 ? (
										<Flex flexDirection='column'>
											<Text as="p" fontWeight='bold' mb={1}>{field.label}</Text>
											<Flex
												key={field.id}
												as="fieldset"
												flexWrap="wrap"
												rowGap={2}
												flexDirection='column'
												columnGap={1.5}
												pb={2}
												mb={4}
												borderBottom={i < field.items.length}
											>
												{field.items.map((item, i) => {
													const { checked, ...restItem } = item

													const { ref, ...rest } = register(field.name)

													return (
														<Flex key={item.value}>
															<Radio
																inputRef={ref}
																id={field.name + "-" + item.value}
																name={field.name}
																label={item.caption}
																value={item.value}
																defaultChecked={
																	field.items?.length > 0 &&
																	field.items[i].checked
																}
																{...restItem}
																{...rest}
															/>
														</Flex>
													)
												})}
											</Flex>
										</Flex>
									) : (
										<Flex key={field.id} mb={2} justifyContent="space-between">
											<Checkbox
												inputRef={ref}
												value={field.value ?? field.label}
												defaultChecked={
													field.items?.length > 0 && field.items[0].checked
												}
												{...fieldProps}
											/>
										</Flex>
									),
								hidden: (
									<input
										ref={ref}
										readOnly
										key={field.value}
										value={field.value}
										{...fieldProps}
									/>
								),
							}[type]
						}
					}
				})}
			</Flex>
			<Flex
				w={1}
				flexDirection="column"
				py={3}
				px={{ _: 4, m: 9 }}
			>
				<Flex
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
				>
					{/* Submit button */}
					<Button style={{ position: 'relative' }} type={loading ? 'button' : "submit"} ml="auto">
						<StyledIcon aria-hidden loading={loading}>
							<Icon icon="ellipse" color='white' />
						</StyledIcon>
						<StyledButtonContent loading={loading}>{translate(translationKeys.save)}</StyledButtonContent>
					</Button>
				</Flex>

				{/* Feedback message - used if we don't redirect (errors & success) */}
				<Flex
					h={feedbackMsg ? "auto" : "0"}
					overflow="hidden"
					w={1}
					mt={1.5}
					justifyContent="flex-end"
					role="region"
					aria-live="polite"
				>
					<Text
						as="span"
						textColor={feedbackMsg?.success ? "primary" : "alert"}
					>
						{feedbackMsg?.message}
					</Text>
				</Flex>
			</Flex>
		</CapioSharedLoginFormWideStyled>
	)
}

export { LifestyleForm }
